import React, { useState } from "react";
import { toast } from "sonner";
import { updateAgentMetricsConfig } from "@/lib/api";

// Fix 8: the savings are an ESTIMATE; show the assumptions next to the figure and let the
// company master (or Inviktus staff) adjust them. Other users only see them.
export const ESTIMATE_TEXT = "Estimación: consultas × minutos por consulta × tarifa. Ajuste los valores a su realidad.";

// "(por defecto)" when the value was not reported by the client in the ABC interview (fix 9)
const srcNote = (src) => (!src || src === "default" ? " (por defecto)" : "");

export default function SavingsAssumptions({ slug, config, canEdit, onSaved, compact = false }) {
  const minutes = config?.minutes_per_interaction ?? 15;
  const rate = config?.hourly_rate_usd ?? null; // fix 12b: null = "no informado" (no default for this currency)
  const currency = config?.currency || "USD"; // fix 9: currency of the agent's company
  const defaults = [config?.minutes_source, config?.hourly_rate_source].some((s) => !s || s === "default");
  const [editing, setEditing] = useState(false);
  const [min, setMin] = useState(String(minutes));
  const [usd, setUsd] = useState(rate == null ? "" : String(rate));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const m = Number(min);
    const r = Number(usd);
    if (!Number.isFinite(m) || !Number.isFinite(r) || m <= 0 || r <= 0) {
      toast.error("Indique minutos y tarifa mayores que cero.");
      return;
    }
    try {
      setSaving(true);
      const d = await updateAgentMetricsConfig(slug, { minutes_per_interaction: m, hourly_rate_usd: r });
      const cfg = d.metrics_config || {};
      setMin(String(cfg.minutes_per_interaction));
      setUsd(cfg.hourly_rate_usd == null ? "" : String(cfg.hourly_rate_usd));
      setEditing(false);
      toast.success("Supuestos actualizados. Las cifras se recalculan con los nuevos valores.");
      if (onSaved) onSaved(cfg);
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error(typeof det === "string" ? det : "No se pudieron guardar los supuestos.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={compact ? "text-[11px] text-slate-500" : "text-xs text-slate-600"} data-testid={`savings-assumptions-${slug}`}>
      {!editing ? (
        <p>
          <span className="font-semibold text-amber-700">Estimado</span> · {minutes} min por consulta{srcNote(config?.minutes_source)} · tarifa{" "}
          {rate == null
            ? <span data-testid={`savings-rate-missing-${slug}`}>{currency}: {config?.hourly_rate_note || "no informado"}</span>
            : <>{currency} {Number(rate).toLocaleString("es-419", { maximumFractionDigits: 2 })}/hora{srcNote(config?.hourly_rate_source)}</>}
          {rate != null && config?.hourly_rate_note && <span className="text-amber-700"> · {config.hourly_rate_note}</span>}
          {config?.hourly_rate_review && (
            <span className="ml-1 font-semibold text-red-700" data-testid={`savings-review-${slug}`}> · {config.hourly_rate_review}</span>
          )}
          {canEdit && (
            <button
              type="button"
              onClick={() => { setMin(String(minutes)); setUsd(rate == null ? "" : String(rate)); setEditing(true); }}
              className="ml-2 font-semibold text-accent-blue hover:underline"
              data-testid={`savings-edit-${slug}`}
            >
              Ajustar
            </button>
          )}
        </p>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <label className="inline-flex items-center gap-1">
            Min/consulta
            <input type="number" min="1" max="240" value={min} onChange={(e) => setMin(e.target.value)}
              className="w-16 border border-slate-300 bg-white px-1 py-0.5 text-slate-900" data-testid={`savings-min-${slug}`} />
          </label>
          <label className="inline-flex items-center gap-1">
            Tarifa {currency}/hora
            <input type="number" min="1" max="10000000" value={usd} onChange={(e) => setUsd(e.target.value)}
              className="w-28 border border-slate-300 bg-white px-1 py-0.5 text-slate-900" data-testid={`savings-usd-${slug}`} />
          </label>
          <button type="button" onClick={save} disabled={saving}
            className="font-semibold text-accent-blue hover:underline disabled:opacity-50" data-testid={`savings-save-${slug}`}>
            {saving ? "Guardando…" : "Guardar"}
          </button>
          <button type="button" onClick={() => setEditing(false)} className="text-slate-500 hover:underline">Cancelar</button>
        </div>
      )}
      {!compact && <p className="mt-1 text-slate-500">{ESTIMATE_TEXT}</p>}
      {!compact && defaults && (
        <p className="mt-1 text-slate-500">«(por defecto)» = valor por defecto, no informado por el cliente en la consultoría.</p>
      )}
    </div>
  );
}
